import React, { useState, FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiPost } from '../api/client';
import type { ResetPasswordRequest, MessageResponse } from '../types/api';
import AlertMessage from '../components/AlertMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import './ResetPasswordPage.css';

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d]).{10,}$/;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (!email || !token) {
      setError('Invalid reset link.');
      return;
    }
    if (!PASSWORD_REGEX.test(newPassword)) {
      setError('Password must be at least 10 characters with uppercase, lowercase, digit, and special character.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const body: ResetPasswordRequest = { email, token, newPassword, confirmPassword };
      const data = await apiPost<MessageResponse>('/api/auth/reset-password', body);
      setSuccess(data.message);
    } catch (err: any) {
      setError(err.message || 'Password reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1 className="auth-title">Cedar Vault</h1>
          <p className="auth-subtitle">Password Reset</p>
          <div className="divider" />
          <AlertMessage type="success" message={success} />
          <div className="auth-links">
            <Link to="/login">Sign in with your new password</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!email || !token) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1 className="auth-title">Cedar Vault</h1>
          <p className="auth-subtitle">Invalid Link</p>
          <div className="divider" />
          <AlertMessage type="error" message="This password reset link is invalid or expired." />
          <div className="auth-links">
            <Link to="/forgot-password">Request a new reset link</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1 className="auth-title">Cedar Vault</h1>
        <p className="auth-subtitle">Set your new password</p>
        <div className="divider" />

        <AlertMessage type="error" message={error} />

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="newPassword">New password</label>
            <input
              id="newPassword"
              className="form-input"
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              required
              autoComplete="new-password"
              autoFocus
            />
            <p className="password-hint">
              At least 10 characters, with uppercase, lowercase, digit, and special character.
            </p>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="confirmNewPassword">Confirm password</label>
            <input
              id="confirmNewPassword"
              className="form-input"
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
          </div>

          <button className="btn-primary" type="submit" disabled={loading}>
            {loading ? <LoadingSpinner small /> : 'Reset Password'}
          </button>
        </form>

        <div className="auth-links">
          <Link to="/login">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
